import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy, Trash2, FileText, ChevronDown, Star, Search, X, Pencil, Check,
  Repeat, Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PLATFORM_META, ALL_PLATFORMS } from "@/lib/platforms";
import { copyWithToast } from "@/lib/clipboard";

const TABS = [
  { key: "all", label: "All" },
  { key: "starred", label: "Starred" },
  { key: "recent", label: "Recent" },
];

export default function History() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  const renameInputRef = useRef(null);
  const [reusingId, setReusingId] = useState(null); // for "Generate New Version" spinner
  const [brandVoiceInfo, setBrandVoiceInfo] = useState({ id: "", name: "" });

  // Filters
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [formatFilter, setFormatFilter] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Fetch current default voice for auto-apply on reuse
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/brand-voices");
        const items = data.items || [];
        if (items.length > 0) {
          setBrandVoiceInfo({ id: items[0].id, name: items[0].name });
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedQ) params.q = debouncedQ;
      if (formatFilter) params.format = formatFilter;
      if (tab === "starred") params.starred = true;
      if (tab === "recent") params.since_days = 7;
      const { data } = await api.get("/history", { params });
      setItems(data.items || []);
    } catch {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, formatFilter, tab]);

  useEffect(() => { load(); }, [load]);

  const clearFilters = () => {
    setQ("");
    setFormatFilter("");
    setTab("all");
  };

  const handleCopy = async (text, label) => {
    await copyWithToast(text, label);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/history/${id}`);
      setItems((cur) => cur.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleStar = async (item) => {
    const prev = items;
    const nextStarred = !item.starred;
    setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, starred: nextStarred } : i)));
    try {
      const { data } = await api.patch(`/history/${item.id}`, { starred: nextStarred });
      setItems((cur) => cur.map((i) => (i.id === item.id ? data.item : i)));
      if (tab === "starred" && !nextStarred) {
        setItems((cur) => cur.filter((i) => i.id !== item.id));
      }
    } catch {
      setItems(prev);
      toast.error("Could not update");
    }
  };

  const startRename = (item) => {
    setRenamingId(item.id);
    setRenameDraft(item.title || defaultTitle(item));
    setTimeout(() => renameInputRef.current?.focus(), 30);
  };
  const cancelRename = () => { setRenamingId(null); setRenameDraft(""); };
  const saveRename = async (item) => {
    const title = renameDraft.trim().slice(0, 120);
    if (!title) { cancelRename(); return; }
    const prev = items;
    setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, title } : i)));
    setRenamingId(null);
    try { await api.patch(`/history/${item.id}`, { title }); toast.success("Renamed"); }
    catch { setItems(prev); toast.error("Rename failed"); }
  };

  // 1) One-click Reuse — send content + formats + voice to Studio (do NOT auto-generate)
  const repurposeAgain = (item, formatsOverride = null) => {
    navigate("/dashboard", {
      state: {
        reuse: {
          content: item.input_content,
          formats: formatsOverride || item.formats,
          brand_voice_id: brandVoiceInfo.id || null,
          brand_voice_name: brandVoiceInfo.name || null,
        },
      },
    });
  };

  // 2) Fresh Variation — call /generate right now, then jump to Studio with the new outputs
  const generateNewVersion = async (item) => {
    setReusingId(item.id);
    try {
      const useVoice = !!brandVoiceInfo.id;
      const { data } = await api.post("/generate", {
        content: item.input_content,
        formats: item.formats,
        use_brand_voice: useVoice,
        brand_voice_id: useVoice ? brandVoiceInfo.id : null,
      });
      navigate("/dashboard", {
        state: {
          reuse: {
            content: item.input_content,
            formats: item.formats,
            brand_voice_id: data.brand_voice_id || null,
            brand_voice_name: data.brand_voice_name,
          },
          outputs: data.generation.outputs,
        },
      });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Generation failed";
      if (status === 402) {
        toast.error("Free plan limit reached. Upgrade to Pro for unlimited remixes.");
        setTimeout(() => navigate("/pricing"), 800);
      } else {
        toast.error(detail);
      }
    } finally {
      setReusingId(null);
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const defaultTitle = (item) => {
    const s = (item.input_content || "").trim();
    return s ? `${s.slice(0, 60)}${s.length > 60 ? "…" : ""}` : "Untitled remix";
  };

  const activeFiltersCount =
    (debouncedQ ? 1 : 0) + (formatFilter ? 1 : 0) + (tab !== "all" ? 1 : 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="mx-auto max-w-5xl px-6 py-12 lg:px-10">
        <div className="mb-8">
          <p className="mb-2 text-xs uppercase tracking-[0.25em] text-neutral-500">— The archive</p>
          <h1 className="font-serif text-4xl font-bold tracking-tight sm:text-5xl">History</h1>
          <p className="mt-3 text-sm text-neutral-600">
            Every remix you&apos;ve made. Reuse the input, remix into new formats, or spin up a fresh variation.
          </p>
        </div>

        {/* Segmented tabs */}
        <div className="mb-4 flex" data-testid="history-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`history-tab-${t.key}`}
              className={`flex-1 border py-3 text-sm font-medium transition-colors md:flex-none md:px-8 ${
                tab === t.key
                  ? "border-coral bg-coral-subtle text-coral"
                  : "border-neutral-200 bg-white text-neutral-600 hover:border-black hover:text-black"
              } -ml-px first:ml-0`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filter row */}
        <div className="mb-4 border border-neutral-200 bg-white p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" strokeWidth={1.5} />
            <Input
              data-testid="history-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search input, title or output text…"
              className="h-11 rounded-none border-neutral-200 pl-10 pr-10 focus-visible:ring-1 focus-visible:ring-black"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                data-testid="history-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-black"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" strokeWidth={1.5} />
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-neutral-500">Format:</span>
            <button
              type="button"
              onClick={() => setFormatFilter("")}
              data-testid="history-filter-format-all"
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                formatFilter === ""
                  ? "border-coral bg-coral-subtle text-coral"
                  : "border-neutral-300 bg-white text-neutral-700 hover:border-black hover:text-black"
              }`}
            >
              All
            </button>
            {ALL_PLATFORMS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setFormatFilter(formatFilter === key ? "" : key)}
                data-testid={`history-filter-format-${key}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  formatFilter === key
                    ? "border-coral bg-coral-subtle text-coral"
                    : "border-neutral-300 bg-white text-neutral-700 hover:border-black hover:text-black"
                }`}
              >
                {PLATFORM_META[key].short}
              </button>
            ))}
            {activeFiltersCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                data-testid="history-clear-filters"
                className="ml-auto text-xs text-neutral-500 underline underline-offset-4 hover:text-black"
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {!loading && (
          <p className="mb-4 text-xs uppercase tracking-widest text-neutral-500" data-testid="history-result-count">
            {items.length} result{items.length !== 1 ? "s" : ""}
            {activeFiltersCount > 0 ? " (filtered)" : ""}
          </p>
        )}

        {loading ? (
          <div className="flex justify-center py-24" data-testid="history-loading">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-neutral-300 bg-white p-16 text-center" data-testid="history-empty">
            <FileText className="mx-auto mb-4 h-8 w-8 text-neutral-500" strokeWidth={1.5} />
            <p className="font-serif text-2xl italic text-neutral-500">
              {activeFiltersCount > 0 ? "No matches." : "Your remixes will live here."}
            </p>
            <p className="mt-2 text-sm text-neutral-500">
              {activeFiltersCount > 0
                ? "Try clearing the filters or search."
                : "Every generation is saved automatically. Star favorites, rename them, and repurpose in one tap."}
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              {activeFiltersCount > 0 ? (
                <Button
                  onClick={clearFilters}
                  data-testid="history-empty-clear-btn"
                  className="h-11 rounded-[3px] bg-coral px-6 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
                >
                  Clear filters
                </Button>
              ) : (
                <Button
                  onClick={() => navigate("/dashboard")}
                  data-testid="history-empty-cta"
                  className="h-11 rounded-[3px] bg-coral px-6 text-white transition-all duration-300 hover:bg-coral-hover hover:shadow-coral"
                >
                  <Sparkles className="mr-2 h-4 w-4" strokeWidth={1.5} />
                  Create your first remix
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => {
              const isOpen = expanded === item.id;
              const isRenaming = renamingId === item.id;
              const isReusing = reusingId === item.id;
              const displayTitle = item.title || defaultTitle(item);
              return (
                <div
                  key={item.id}
                  data-testid={`history-item-${item.id}`}
                  className={`border bg-white transition-colors ${item.starred ? "border-black" : "border-neutral-200"}`}
                >
                  <div className="flex items-start justify-between gap-4 p-6">
                    <div className="min-w-0 flex-1">
                      {isRenaming ? (
                        <div className="mb-2 flex items-center gap-2">
                          <Input
                            ref={renameInputRef}
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveRename(item);
                              if (e.key === "Escape") cancelRename();
                            }}
                            maxLength={120}
                            data-testid={`history-rename-input-${item.id}`}
                            className="h-9 rounded-none border-neutral-300 font-serif text-lg focus-visible:ring-1 focus-visible:ring-black"
                          />
                          <button
                            type="button"
                            onClick={() => saveRename(item)}
                            data-testid={`history-rename-save-${item.id}`}
                            className="p-2 text-neutral-600 hover:text-black"
                            aria-label="Save rename"
                          >
                            <Check className="h-4 w-4" strokeWidth={1.5} />
                          </button>
                          <button
                            type="button"
                            onClick={cancelRename}
                            data-testid={`history-rename-cancel-${item.id}`}
                            className="p-2 text-neutral-500 hover:text-black"
                            aria-label="Cancel rename"
                          >
                            <X className="h-4 w-4" strokeWidth={1.5} />
                          </button>
                        </div>
                      ) : (
                        <div className="mb-2 flex items-start gap-2">
                          <h3
                            data-testid={`history-title-${item.id}`}
                            className="line-clamp-2 min-w-0 flex-1 break-words font-serif text-lg font-semibold tracking-tight text-neutral-900"
                          >
                            {displayTitle}
                          </h3>
                          <button
                            type="button"
                            onClick={() => startRename(item)}
                            data-testid={`history-rename-btn-${item.id}`}
                            className="mt-1 flex-shrink-0 p-1 text-neutral-500 hover:text-black"
                            aria-label="Rename"
                          >
                            <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                      )}

                      <p className="text-xs uppercase tracking-widest text-neutral-500">
                        {fmtDate(item.created_at)} · {item.formats.length} format{item.formats.length > 1 ? "s" : ""}
                        {item.brand_voice_name && <> · voice: {item.brand_voice_name}</>}
                      </p>

                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setExpanded(isOpen ? null : item.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setExpanded(isOpen ? null : item.id);
                          }
                        }}
                        data-testid={`history-toggle-${item.id}`}
                        className="mt-2 cursor-pointer"
                      >
                        <p className="line-clamp-2 text-sm text-neutral-600">
                          {item.input_content.slice(0, 220)}
                          {item.input_content.length > 220 ? "…" : ""}
                        </p>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {item.formats.map((f) => (
                          <span key={f} className="border border-neutral-200 px-2 py-0.5 text-xs text-neutral-600">
                            {PLATFORM_META[f]?.short || f}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Right column: quick actions (star / chevron / delete) */}
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => toggleStar(item)}
                        data-testid={`history-star-${item.id}`}
                        className={`p-2 ${item.starred ? "text-black" : "text-neutral-500 hover:text-black"}`}
                        aria-label={item.starred ? "Unstar" : "Star"}
                        aria-pressed={item.starred}
                      >
                        <Star className={`h-4 w-4 ${item.starred ? "fill-black" : ""}`} strokeWidth={1.5} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : item.id)}
                        data-testid={`history-chevron-${item.id}`}
                        className="p-2 text-neutral-500 hover:text-black"
                        aria-label={isOpen ? "Collapse" : "Expand"}
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
                          strokeWidth={1.5}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        data-testid={`history-delete-${item.id}`}
                        className="p-2 text-neutral-500 hover:text-black"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>

                  {/* Primary action row: Repurpose Again + Generate New Version + change-format chips */}
                  <div className="flex flex-col gap-3 border-t border-neutral-100 bg-neutral-50 p-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs uppercase tracking-widest text-neutral-500">
                        Remix into:
                      </span>
                      {ALL_PLATFORMS.map((key) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => repurposeAgain(item, [key])}
                          data-testid={`history-remix-into-${item.id}-${key}`}
                          className="rounded-full border border-neutral-300 bg-white px-3 py-1 text-xs text-neutral-700 transition-colors hover:border-black hover:text-black"
                          title={`Repurpose to ${PLATFORM_META[key].label}`}
                        >
                          {PLATFORM_META[key].short}
                        </button>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => repurposeAgain(item)}
                        data-testid={`history-repurpose-${item.id}`}
                        className="h-10 rounded-none border-neutral-300 text-xs"
                      >
                        <Repeat className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} />
                        Repurpose again
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => generateNewVersion(item)}
                        disabled={isReusing}
                        data-testid={`history-new-version-${item.id}`}
                        className="h-10 rounded-[3px] bg-neutral-900 text-xs text-white hover:bg-neutral-800 disabled:opacity-60"
                      >
                        {isReusing ? (
                          <>
                            <div className="mr-2 h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                            Remixing…
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} />
                            Generate new version
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t border-neutral-100">
                      {item.outputs.map((out) => (
                        <div key={out.format} className="border-b border-neutral-100 p-6 last:border-b-0">
                          <div className="mb-3 flex items-center justify-between">
                            <h4 className="font-serif text-lg font-semibold">
                              {PLATFORM_META[out.format]?.label || out.format}
                            </h4>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCopy(out.text, PLATFORM_META[out.format]?.short || out.format)}
                              data-testid={`history-copy-${item.id}-${out.format}`}
                              className="h-8 rounded-none border-neutral-300 text-xs"
                            >
                              <Copy className="mr-2 h-3 w-3" strokeWidth={1.5} />
                              Copy
                            </Button>
                          </div>
                          <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed text-neutral-700">
                            {out.text}
                          </pre>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
